import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import ApiService from '../services/api';

interface OrderingEzidebitTermsTabProps {
  onError?: (msg: string) => void;
}

const OrderingEzidebitTermsTab: React.FC<OrderingEzidebitTermsTabProps> = ({ onError }) => {
  const [creditCardTerms, setCreditCardTerms] = useState('');
  const [directDebitTerms, setDirectDebitTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLocalError(null);
      const data = await ApiService.adminGetEzidebitPaymentTerms();
      setCreditCardTerms(data.credit_card_terms ?? '');
      setDirectDebitTerms(data.direct_debit_terms ?? '');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to load ordering payment terms';
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(null);
      setLocalError(null);
      const data = await ApiService.adminUpdateEzidebitPaymentTerms({
        credit_card_terms: creditCardTerms,
        direct_debit_terms: directDebitTerms,
      });
      setCreditCardTerms(data.credit_card_terms ?? '');
      setDirectDebitTerms(data.direct_debit_terms ?? '');
      setSuccess('Saved successfully.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save';
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Ezidebit payment terms (public order flow)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These texts appear on the Payment step of the internet order page. Customers must accept the terms
        for their selected payment method (credit card or direct debit) before submitting an order.
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        minRows={14}
        label="Credit card — Ezidebit terms and conditions"
        value={creditCardTerms}
        onChange={(e) => setCreditCardTerms(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{ sx: { fontFamily: 'inherit', alignItems: 'flex-start' } }}
        placeholder="Paste or type full terms. Shown when the customer selects Credit Card on the Payment step."
      />

      <TextField
        fullWidth
        multiline
        minRows={14}
        label="Direct debit — Ezidebit terms and conditions"
        value={directDebitTerms}
        onChange={(e) => setDirectDebitTerms(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{ sx: { fontFamily: 'inherit', alignItems: 'flex-start' } }}
        placeholder="Paste or type full terms. Shown when the customer selects Direct Debit on the Payment step."
      />

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Paper>
  );
};

export default OrderingEzidebitTermsTab;
